import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AppContext } from '../context/AppContext';
import { Socio, Carrera, Liga, Sancion, Apelacion, EventoComunitario } from '../models/types';
import { FaTrophy, FaFlag, FaUsers, FaGavel, FaExclamationTriangle } from '../components/Icons';
import { FaCalendarAlt } from 'react-icons/fa';
import { theme } from '../theme';

// Responsive breakpoints
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

const HomeContainer = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px 12px;
    margin: 0;
    min-height: 100vh;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  margin-bottom: 32px;
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.8rem;
    margin-bottom: 24px;
    text-align: center;
    padding-bottom: 16px;
    
    &:after {
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
    }
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 36px;
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const Card = styled.div`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: ${theme.shadows.large};
    transform: translateY(-2px);
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  position: relative;
  overflow: hidden;
  padding-top: 32px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, ${theme.colors.secondary.main}, ${theme.colors.primary.main});
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 20px 16px 24px 16px;
  }
`;

const StatValue = styled.div`
  font-size: 2.8rem;
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.main};
  margin: 16px 0;
  line-height: 1;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 2.4rem;
    margin: 12px 0;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: 12px;
  font-weight: ${theme.typography.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatIcon = styled.div`
  font-size: 2.2rem;
  color: ${theme.colors.secondary.main};
  margin-bottom: 16px;
  background-color: rgba(228, 49, 46, 0.1);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 50px;
    height: 50px;
    font-size: 1.8rem;
    margin: 0 auto 12px;
  }
`;

const FullWidthCard = styled(Card)`
  grid-column: 1 / -1;
  margin-bottom: 24px;
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 16px;
    padding: 16px;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 16px;
  border-radius: ${theme.borderRadius.small};
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #ccc;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th, td {
    padding: 16px;
    text-align: left;
  }
  
  th {
    background-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrastText};
    font-weight: ${theme.typography.fontWeights.semibold};
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  th:first-child {
    border-top-left-radius: ${theme.borderRadius.small};
  }
  
  th:last-child {
    border-top-right-radius: ${theme.borderRadius.small};
  }
  
  tr:last-child td:first-child {
    border-bottom-left-radius: ${theme.borderRadius.small};
  }
  
  tr:last-child td:last-child {
    border-bottom-right-radius: ${theme.borderRadius.small};
  }
  
  tr:nth-child(even) td {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
  
  tr:hover td {
    background-color: rgba(228, 49, 46, 0.05);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    th, td {
      padding: 12px 8px;
      font-size: 0.9rem;
    }
    
    th {
      font-size: 0.8rem;
      padding: 10px 8px;
    }
    
    /* Ocultar algunas columnas en móviles para ahorrar espacio */
    th:nth-child(n+4), 
    td:nth-child(n+4) {
      display: none;
    }
  }
`;

const Badge = styled.span<{ $type: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: ${theme.typography.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: ${props => {
    switch (props.$type) {
      case 'pendiente': return theme.colors.warning.main;
      case 'aceptada': return theme.colors.success.main;
      case 'rechazada': return theme.colors.error.main;
      case 'programado': return theme.colors.info.main;
      case 'en_curso': return theme.colors.warning.main;
      case 'finalizado': return theme.colors.success.main;
      case 'cancelado': return theme.colors.error.main;
      default: return '#9E9E9E';
    }
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 4px 8px;
    font-size: 0.7rem;
    letter-spacing: 0.3px;
  }
`;

const CardTitle = styled.h2`
  color: ${theme.colors.primary.main};
  margin-bottom: 20px;
  font-size: 1.4rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 16px;
    
    svg {
      margin-right: 8px;
      font-size: 1.1rem;
    }
  }
`;

const CardContent = styled.div`
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  flex: 1;
  
  p {
    color: ${theme.colors.text.secondary};
    font-size: 1rem;
    text-align: center;
    margin-top: 24px;
  }
`;

const Home: React.FC = () => {
  const { data } = useContext(AppContext);
  const [proximasCarreras, setProximasCarreras] = useState<Carrera[]>([]);
  const [proximosEventos, setProximosEventos] = useState<EventoComunitario[]>([]);
  const [apelacionesPendientes, setApelacionesPendientes] = useState<Apelacion[]>([]);
  
  useEffect(() => {
    // Filtrar próximas carreras (fecha >= hoy)
    const today = new Date().toISOString().split('T')[0];
    
    if (data.carreras) {
      const proximas = data.carreras
        .filter((carrera: Carrera) => carrera.fecha >= today)
        .sort((a: Carrera, b: Carrera) => a.fecha.localeCompare(b.fecha))
        .slice(0, 5); // Mostrar solo las 5 próximas
      
      setProximasCarreras(proximas);
    }
    
    // Filtrar próximos eventos comunitarios
    if (data.eventosComunitarios) {
      const proximos = data.eventosComunitarios
        .filter((evento: EventoComunitario) => evento.fecha >= today && evento.estado === 'programado')
        .sort((a: EventoComunitario, b: EventoComunitario) => a.fecha.localeCompare(b.fecha))
        .slice(0, 5);
      
      setProximosEventos(proximos);
    }
    
    // Filtrar apelaciones pendientes
    if (data.apelaciones) {
      const pendientes = data.apelaciones
        .filter((apelacion: Apelacion) => apelacion.estado === 'pendiente')
        .sort((a: Apelacion, b: Apelacion) => b.fechaApelacion.localeCompare(a.fechaApelacion))
        .slice(0, 5);
      
      setApelacionesPendientes(pendientes);
    }
  }, [data]);
  
  // Función para obtener nombre de piloto
  const getNombrePiloto = (socioId: string) => {
    const socio = data.socios?.find((s: Socio) => s.id === socioId);
    return socio ? `${socio.nombre} ${socio.apellido}` : 'Piloto desconocido';
  };
  
  // Función para obtener nombre de liga
  const getNombreLiga = (ligaId: string) => {
    const liga = data.ligas?.find((l: Liga) => l.id === ligaId);
    return liga ? liga.nombre : 'Liga desconocida';
  };
  
  return (
    <HomeContainer>
      <Title>Dashboard VRSimracers Manager</Title>
      
      {/* Estadísticas generales */}
      <DashboardGrid>
        <StatCard>
          <StatIcon><FaUsers /></StatIcon>
          <StatValue>{data.socios?.length || 0}</StatValue>
          <StatLabel>Pilotos registrados</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon><FaTrophy /></StatIcon>
          <StatValue>{data.ligas?.length || 0}</StatValue>
          <StatLabel>Ligas activas</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon><FaFlag /></StatIcon>
          <StatValue>{data.carreras?.length || 0}</StatValue>
          <StatLabel>Carreras totales</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon><FaGavel /></StatIcon>
          <StatValue>{data.sanciones?.length || 0}</StatValue>
          <StatLabel>Sanciones aplicadas</StatLabel>
        </StatCard>
      </DashboardGrid>
      
      {/* Próximas carreras */}
      <FullWidthCard>
        <CardTitle><FaFlag /> Próximas Carreras</CardTitle>
        <CardContent>
          {proximasCarreras.length > 0 ? (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Nombre</th>
                    <th>Liga</th>
                    <th>Circuito</th>
                  </tr>
                </thead>
                <tbody>
                  {proximasCarreras.map((carrera: Carrera) => (
                    <tr key={carrera.id}>
                      <td>{new Date(carrera.fecha).toLocaleDateString()}</td>
                      <td>{carrera.nombre}</td>
                      <td>{getNombreLiga(carrera.ligaId)}</td>
                      <td>{carrera.circuito}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <p>No hay carreras programadas próximamente.</p>
          )}
        </CardContent>
      </FullWidthCard>
      
      {/* Próximos eventos comunitarios */}
      <FullWidthCard>
        <CardTitle><FaCalendarAlt /> Próximos Eventos Comunitarios</CardTitle>
        <CardContent>
          {proximosEventos.length > 0 ? (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {proximosEventos.map((evento: EventoComunitario) => (
                    <tr key={evento.id}>
                      <td>{new Date(evento.fecha).toLocaleDateString()}</td>
                      <td>{evento.nombre}</td>
                      <td>{evento.tipo.replace('_', ' ')}</td>
                      <td><Badge $type={evento.estado}>{evento.estado}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <p>No hay eventos comunitarios programados próximamente.</p>
          )}
        </CardContent>
      </FullWidthCard>
      
      {/* Apelaciones pendientes */}
      <FullWidthCard>
        <CardTitle><FaExclamationTriangle /> Apelaciones Pendientes</CardTitle>
        <CardContent>
          {apelacionesPendientes.length > 0 ? (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Piloto</th>
                    <th>Motivo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {apelacionesPendientes.map((apelacion: Apelacion) => (
                    <tr key={apelacion.id}>
                      <td>{new Date(apelacion.fechaApelacion).toLocaleDateString()}</td>
                      <td>{getNombrePiloto(apelacion.socioId)}</td>
                      <td>{apelacion.motivo.substring(0, 50)}...</td>
                      <td><Badge $type={apelacion.estado}>{apelacion.estado}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <p>No hay apelaciones pendientes de revisión.</p>
          )}
        </CardContent>
      </FullWidthCard>
    </HomeContainer>
  );
};

export default Home;
